import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, FileText, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';

interface TimeOffRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  resourceId: string;
}

interface TimeOffRequest {
  start_date: string;
  end_date: string;
  request_type: string;
  is_partial_day: boolean;
  start_time?: string;
  end_time?: string;
  notes: string;
  status: 'pending' | 'approved' | 'denied';
}

const TIME_OFF_TYPES = [
  { value: 'vacation', label: 'Vacation', color: 'bg-blue-100 text-blue-800' },
  { value: 'sick', label: 'Sick Leave', color: 'bg-red-100 text-red-800' },
  { value: 'personal', label: 'Personal Day', color: 'bg-purple-100 text-purple-800' },
  { value: 'family', label: 'Family Emergency', color: 'bg-orange-100 text-orange-800' },
  { value: 'bereavement', label: 'Bereavement', color: 'bg-gray-100 text-gray-800' },
  { value: 'other', label: 'Other', color: 'bg-green-100 text-green-800' }
];

export default function TimeOffRequestModal({
  isOpen,
  onClose,
  onSuccess,
  resourceId
}: TimeOffRequestModalProps) {
  const { selectedBusiness } = useBusiness();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [requestType, setRequestType] = useState('vacation');
  const [isPartialDay, setIsPartialDay] = useState(false);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [notes, setNotes] = useState('');

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  // Open animation
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  const handleTransitionEnd = () => {
    if (!isVisible && !isOpen) {
      setIsAnimating(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(), 200);
  };

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      setStartDate(format(today, 'yyyy-MM-dd'));
      setEndDate(format(tomorrow, 'yyyy-MM-dd'));
      setRequestType('vacation');
      setIsPartialDay(false);
      setStartTime('09:00');
      setEndTime('17:00');
      setNotes('');
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedBusiness?.id || !resourceId || !supabase) {
      setError('Missing required information');
      return;
    }

    if (!startDate || !endDate) {
      setError('Please select start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setError('End date must be after start date');
      return;
    }

    if (isPartialDay && (!startTime || !endTime)) {
      setError('Please specify start and end times for partial day requests');
      return;
    }

    if (isPartialDay && startTime >= endTime) {
      setError('End time must be after start time');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const requestData: Partial<TimeOffRequest> = {
        start_date: startDate,
        end_date: endDate,
        request_type: requestType,
        is_partial_day: isPartialDay,
        notes: notes.trim(),
        status: 'pending'
      };

      if (isPartialDay) {
        requestData.start_time = startTime;
        requestData.end_time = endTime;
      }

      const { error: insertError } = await supabase
        .from('time_off_requests')
        .insert({
          ...requestData,
          business_id: selectedBusiness.id,
          resource_id: resourceId,
          requested_at: new Date().toISOString()
        });

      if (insertError) {
        console.error('Error creating time off request:', insertError);
        setError('Failed to submit time off request');
        return;
      }

      onSuccess?.();
      handleClose();
    } catch (err) {
      console.error('Error submitting time off request:', err);
      setError('Failed to submit time off request');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  return createPortal(
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isVisible ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{ 
        zIndex: 100200,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)'
      }}
      onClick={handleClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`bg-white transform transition-all duration-200 overflow-hidden ${
          isMobile 
            ? `fixed left-0 right-0 w-full ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isVisible ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100210,
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          ...(isMobile && { left: 0, right: 0, maxWidth: '100vw' }),
          ...(!isMobile && { height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' }),
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative flex-shrink-0">
            <h2 className="text-2xl font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
              Request Time Off
            </h2>
            <p className="text-sm text-gray-500 mt-1">Submit a time off request for approval</p>
          </div>

          {/* Content */}
          <form 
            id="time-off-form"
            onSubmit={handleSubmit} 
            className="flex-1 overflow-y-auto overflow-x-hidden bg-white"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-5`}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* Date Range */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Date Range
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="min-w-0">
                    <label className="block text-xs text-gray-500 mb-1.5">Start Date</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      required
                      className="w-full max-w-full px-3 py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 box-border"
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block text-xs text-gray-500 mb-1.5">End Date</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      required
                      min={startDate}
                      className="w-full max-w-full px-3 py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 box-border"
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                </div>
              </div>

              {/* Request Type */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Request Type
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                  {TIME_OFF_TYPES.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setRequestType(type.value)}
                      className={`p-3 rounded-xl border-2 transition-all duration-150 text-sm font-medium touch-manipulation active:scale-[0.97] min-w-0 ${
                        requestType === type.value
                          ? 'border-blue-500 bg-blue-50 shadow-sm'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <span className={`inline-block px-2 py-1 rounded-lg text-xs font-semibold ${type.color}`}>
                        {type.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Partial Day Option */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                <div className="flex items-center space-x-3 mb-3">
                  <input
                    type="checkbox"
                    id="partialDay"
                    checked={isPartialDay}
                    onChange={(e) => setIsPartialDay(e.target.checked)}
                    className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded-md flex-shrink-0"
                  />
                  <label htmlFor="partialDay" className="text-sm font-semibold text-gray-700">
                    Partial Day Request
                  </label>
                </div>
                
                {isPartialDay && (
                  <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-100">
                    <div className="min-w-0">
                      <label className="block text-xs text-gray-500 mb-1.5">Start Time</label>
                      <input
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        required={isPartialDay}
                        className="w-full max-w-full px-3 py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 box-border"
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block text-xs text-gray-500 mb-1.5">End Time</label>
                      <input
                        type="time"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        required={isPartialDay}
                        min={startTime}
                        className="w-full max-w-full px-3 py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 box-border"
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Notes <span className="font-normal text-gray-400">(Optional)</span>
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Add any additional details about your time off request..."
                  className="w-full max-w-full px-3 py-3 text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 resize-none box-border"
                  style={{ fontSize: '16px' }}
                />
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className="border-t border-gray-200 bg-white px-4 py-3 flex-shrink-0" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0.75rem))' }}>
            <div className="flex gap-2.5">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 active:bg-gray-100 active:scale-[0.98] disabled:opacity-50 text-[15px] font-semibold transition-all duration-150 touch-manipulation whitespace-nowrap"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="time-off-form"
                disabled={loading}
                className="flex-1 py-2.5 px-4 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 active:scale-[0.98] disabled:opacity-50 text-white rounded-xl text-[15px] font-semibold shadow-sm transition-all duration-150 touch-manipulation flex items-center justify-center gap-1.5 whitespace-nowrap"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin flex-shrink-0" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 flex-shrink-0" />
                    <span>Submit</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
